import React from 'react';
import { useRouter } from 'next/router';
import { MEMES_SUB_MENU, ModeValue, SubModeValue, TimeFrames } from '@shared/types/frontend/searchHome';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { IoTriangle } from 'react-icons/io5';
import { useSession } from '@/contexts/LitSessionProvider';
import { CATEGORIES, EVENTS, LogToMixpanel } from '@/lib/misc/mixpanel';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import ImageWithFallback from '@/components/shared/ImageWithFallback';
import TokenImage from '@/components/shared/TokenImage';
import classNames from '@/lib/misc/classNames';
interface FilterMemeModeProps {
  setMode: (_: ModeValue) => void;
  subMode: SubModeValue;
  setSubMode: React.Dispatch<React.SetStateAction<SubModeValue>>;
  timeFrame: TimeFrames;
  subMemeData: any;
  network: number[];
}
const TrendingMemeToggleGroup = ({
  mode,
  setSubMode
}) => {
  const handleToggle = (value: string) => {
    setSubMode(value);
  };
  return <ScrollArea className='flex w-[100%]' data-sentry-element="ScrollArea" data-sentry-component="TrendingMemeToggleGroup" data-sentry-source-file="FilterMemeMode.tsx">
      <ScrollBar orientation='horizontal' className='hidden' data-sentry-element="ScrollBar" data-sentry-source-file="FilterMemeMode.tsx" />

      <ToggleGroup type='single' value={mode} onValueChange={handleToggle} className='flex justify-start gap-4 mb-4 px-4' data-sentry-element="ToggleGroup" data-sentry-source-file="FilterMemeMode.tsx">
        {MEMES_SUB_MENU.map(item => <ToggleGroupItem key={item.label} value={item.modeValue} className={`
              ${item.modeValue === mode && 'bg-genius-blue'}
              disabled:opacity-100
              text-[20px]
              flex
              gap-2
              items-center
              rounded-[12px]
              text-genius-cream
              w-fit
              h-[51px]
              py-[3px]
              px-5
              border
              border-genius-blue
              data-[state=off]:hover:bg-genius-blue`} disabled={item.modeValue === mode}>
            {item.isImage ? <ImageWithFallback src={`/icons/${item.isImage}`} alt={`img for ${item.label}`} width={20} height={20} /> : item.img}
            <p>{item.label}</p>
          </ToggleGroupItem>)}
      </ToggleGroup>
    </ScrollArea>;
};
const TodayTopGainers = ({
  memeMode,
  subMemeData,
  timeFrame,
  network
}) => {
  const {
    PKP
  } = useSession();
  const postiveGainers = JSON.parse(JSON.stringify(subMemeData))?.sort((a, b) => parseFloat(b[`change${timeFrame}`]) - parseFloat(a[`change${timeFrame}`])).filter((token, idx, self) => self.findIndex(t => t?.token?.address === token?.token?.address) === idx).filter(token => parseFloat(token[`change${timeFrame}`]) > 0)?.slice(0, 7)?.map(token => {
    return {
      ...token,
      name: token?.token?.symbol,
      image: token?.token?.imageSmallUrl,
      percent: (parseFloat(token[`change${timeFrame}`]) * 100)?.toFixed(2)
    };
  });
  const negativeGainers = JSON.parse(JSON.stringify(subMemeData))?.sort((a, b) => parseFloat(a[`change${timeFrame}`]) - parseFloat(b[`change${timeFrame}`])).filter((token, idx, self) => self.findIndex(t => t?.token?.address === token?.token?.address) === idx).filter(token => parseFloat(token[`change${timeFrame}`]) < 0)?.slice(0, 7)?.map(token => {
    return {
      ...token,
      name: token?.token?.symbol,
      image: token?.token?.imageSmallUrl,
      percent: (parseFloat(token[`change${timeFrame}`]) * 100)?.toFixed(2)
    };
  });
  const tokens = postiveGainers.concat(negativeGainers);
  const router = useRouter();
  const _handleOnGainerClick = token => {
    const tokenItem = token.token;
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.search.clickTopToken,
      category: CATEGORIES.search,
      metaData: {
        tokenAddress: tokenItem?.address,
        networkId: tokenItem?.networkId
      }
    });
    router.push(`/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}`);
  };
  if (!memeMode?.label) return;
  return <div className='grid gap-2 overflow-hidden no-scrollbar' data-sentry-component="TodayTopGainers" data-sentry-source-file="FilterMemeMode.tsx">
      <div className='flex items-center pl-4'>
        <p className='text-[20px] text-genius-gradient'>Movers</p>
        <p className='ml-1 text-xs'>
          {network?.length == 1 ? `on  ${V2Networks[network?.[0]].name}` : ''}
        </p>
      </div>
      <ScrollArea className='h-[65px] no-scrollbar' data-sentry-element="ScrollArea" data-sentry-source-file="FilterMemeMode.tsx">
        <div className='flex gap-2 px-4'>
          {tokens.map((token, index) => <div key={index}>
              <Button className='h-[51px] w-auto bg-genius-purple rounded-full hover:opacity-80 transition-opacity' onClick={() => _handleOnGainerClick(token)}>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center justify-between w-[40px]'>
                    <TokenImage mainImageSrc={token?.image?.replace('large', 'small')} badgeImageSrc={getNetworkLogo(token?.token?.networkId)} size={32} badgeSize={16} />
                  </div>
                  <p className='text-[16px] text-genius-cream'>{token.name}</p>
                  <div className={classNames('flex gap-1 ml-4 items-center text-green-400', parseFloat(token.percent) < 0 && 'text-red-400')}>
                    <IoTriangle className={classNames(parseFloat(token.percent) < 0 ? 'transform rotate-180' : 'transform rotate-0')} size={16} />
                    <p className='text-[16px]'>{token.percent}%</p>
                  </div>
                </div>
              </Button>
            </div>)}
        </div>

        <ScrollBar orientation='horizontal' className='hidden' data-sentry-element="ScrollBar" data-sentry-source-file="FilterMemeMode.tsx" />
      </ScrollArea>
    </div>;
};
const FilterMemeMode = ({
  subMode,
  setSubMode,
  timeFrame,
  subMemeData,
  network
}: FilterMemeModeProps) => {
  return <div className='grid justify-items-start' data-sentry-component="FilterMemeMode" data-sentry-source-file="FilterMemeMode.tsx">
      <TrendingMemeToggleGroup mode={subMode} setSubMode={setSubMode} data-sentry-element="TrendingMemeToggleGroup" data-sentry-source-file="FilterMemeMode.tsx" />
      {subMemeData && <TodayTopGainers timeFrame={timeFrame} subMemeData={subMemeData} network={network} memeMode={MEMES_SUB_MENU.find(item => item.modeValue === subMode)} />}
    </div>;
};
export default FilterMemeMode;